import { useEffect, useRef } from 'react'

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  if (el.isContentEditable) return true
  const tag = el.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  // Monaco's hidden textarea and other editors
  return !!el.closest('.monaco-editor, [role="textbox"]')
}

export interface Hotkey {
  /** "mod+k", "[", or a two-key sequence like "g d". "mod" is Cmd on macOS, Ctrl elsewhere. */
  keys: string
  handler: (e: KeyboardEvent) => void
  /** Fire even while typing in an input (only sensible for mod+ combos). */
  allowInInputs?: boolean
}

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
export const modKeyLabel = isMac ? '⌘' : 'Ctrl+'

function matchesCombo(e: KeyboardEvent, combo: string): boolean {
  const parts = combo.toLowerCase().split('+')
  const key = parts[parts.length - 1]
  const mod = parts.includes('mod')
  const shift = parts.includes('shift')
  if (mod !== (isMac ? e.metaKey : e.ctrlKey)) return false
  if (!mod && (e.metaKey || e.ctrlKey || e.altKey)) return false
  if (shift !== e.shiftKey && key !== '?') return false
  return e.key.toLowerCase() === key
}

/** Global keyboard shortcuts. Sequences ("g d") must complete within 1 second. */
export function useHotkeys(hotkeys: Hotkey[]): void {
  const ref = useRef(hotkeys)
  useEffect(() => {
    ref.current = hotkeys
  })
  useEffect(() => {
    let pending: string | null = null
    let timer: number | undefined
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return
      const typing = isTypingTarget(e.target)
      for (const hk of ref.current) {
        if (typing && !hk.allowInInputs) continue
        const seq = hk.keys.split(' ')
        if (seq.length === 2) {
          if (pending === seq[0] && matchesCombo(e, seq[1]!)) {
            e.preventDefault()
            pending = null
            hk.handler(e)
            return
          }
        } else if (matchesCombo(e, hk.keys)) {
          e.preventDefault()
          hk.handler(e)
          return
        }
      }
      if (!typing && !e.metaKey && !e.ctrlKey && !e.altKey && e.key.length === 1) {
        pending = e.key.toLowerCase()
        window.clearTimeout(timer)
        timer = window.setTimeout(() => (pending = null), 1000)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.clearTimeout(timer)
    }
  }, [])
}
